import React, { Fragment, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { makeStyles } from "@material-ui/core/styles";
import Avatar from "@material-ui/core/Avatar";
import Box from "@material-ui/core/Box";
import Container from "@material-ui/core/Container";
import { Accordion, AccordionSummary, AccordionDetails } from '@material-ui/core';
import Fab from "@material-ui/core/Fab";
import IconButton from "@material-ui/core/IconButton";
import Paper from "@material-ui/core/Paper";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableRow from "@material-ui/core/TableRow";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import Typography from "@material-ui/core/Typography";

import AddIcon from "@material-ui/icons/Add";
import DeleteIcon from "@material-ui/icons/Delete";
import EditIcon from "@material-ui/icons/Edit";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";

import ComponentLoader from "../../components/loader/ComponentLoader";
import { showErrorAlert } from "../../store/actions/alerts";
import pageTitle from "../../utils/pageTitle";
import makeApiRequest from "../../utils/axios/new-axios";

import {
    ACL_GET_ROLES_LIST_URL,
} from "../../config";

import { permissions, types, checkAcl } from "../../utils/acl";
import AddEditRoleForm from "../../components/acl/AddEditRoleForm";
import DeleteRoleForm from "../../components/acl/DeleteRoleForm";

import { updatePermissionsList } from "../../store/actions/appdata";


const useStyles = makeStyles(theme => ({
    avatar: {
        marginRight: 10,
        width: 27,
        height: 27,
        fontSize: 15,
        fontWeight: "bold",
        color: "#fff"
    },
    heading: {
        fontSize: theme.typography.pxToRem(15),
        flexBasis: "33.33%",
        flexShrink: 0
    },
    paper: {
        width: "100%",
        overflowX: "auto",
        marginBottom: theme.spacing(2),
        backgroundColor: "#212121",
        padding: "15px"
    }
}));


export default function Roles() {
    pageTitle("Admin Roles");
    const dispatch = useDispatch();
    const classes = useStyles();

    const permissionsList = useSelector(state =>
        !!state.appdata.permissions.list
            ? state.appdata.permissions.list
            : []
    );

    const EDIT_ACCESS = checkAcl({ p: permissions.ADMIN_WEB_USER_MANAGEMENT, t: types.VIEW_AND_EDIT });
    const DELETE_ACCESS = checkAcl({ p: permissions.ADMIN_WEB_USER_MANAGEMENT, t: types.VIEW_EDIT_AND_DELETE });

    const [isLoading, setIsloading] = useState(true);
    const [roles, setRoles] = useState(null);

    useEffect(() => {
        if (permissionsList.length === 0) {
            setIsloading(true);
            dispatch(updatePermissionsList());
        } else {
            setIsloading(false);
        }
    }, [permissionsList, dispatch]);

    //Expansion Panel
    const [expanded, setExpanded] = useState(false);
    const handleChange = panel => (event, isExpanded) => {
        setExpanded(isExpanded ? panel : false);
    };

    useEffect(() => {
        makeApiRequest({
            url: ACL_GET_ROLES_LIST_URL.url,
            method: ACL_GET_ROLES_LIST_URL.method,
        })
            .then((response) => {
                setIsloading(false);
                setRoles(response.data);
            })
            .catch((error) => {
                setIsloading(false);
                dispatch(
                    showErrorAlert(
                        "Roles Fetch Error : " +
                        (!!error.response
                            ? `${error.response.status}! - ${error.response.data.errorMessage}`
                            : error.message)
                    )
                );
            });
        // eslint-disable-next-line
    }, []); // [] is required to make sure that it runs only once

    //Add Edit User
    const [addEditParams, setAddEditParams] = useState({
        action: "",
        initialValues: {
            name: "",
            permissions: []
        },
        roleId: ""
    });
    const [openModal, setOpenModal] = useState(false);

    function handleOpenModal() {
        setOpenModal(true);
    }

    function handleCloseModal() {
        setOpenModal(false);
    }

    const addButtonClick = () => {
        setAddEditParams({
            action: "add",
            initialValues: {
                name: "",
                permissions: permissionsList.map(p => ({
                    permissionId: p.id,
                    permissionName: p.name,
                    type: 0,
                }))
            },
            roleId: 0
        });
        handleOpenModal();
    };

    const editButtonClick = role => {
        setAddEditParams({
            action: "edit",
            initialValues: {
                name: role.name,
                permissions: role.permissions
            },
            roleId: role.id
        });
        handleOpenModal();
    };

    //Delete Role
    const [deleteParams, setDeleteParams] = useState({
        roleId: null,
        roleName: null
    });
    const [openDeleteModal, setOpenDeleteModal] = useState(false);

    function handleOpenDeleteModal() {
        setOpenDeleteModal(true);
    }

    function handleCloseDeleteModal() {
        setOpenDeleteModal(false);
    }
    const deleteButtonClick = role => {
        setDeleteParams({
            roleName: role.name,
            roleId: role.id
        });
        handleOpenDeleteModal();
    };

    ////

    const getType = (type) => {
        switch (type) {
            case 0:
                return "-- None --";
            case 1:
                return "View";
            case 2:
                return "View & Edit";
            case 3:
                return "View, Edit & Delete";
            default:
                break;
        }
    }

    return (
        <Container style={{ padding: "10px" }}>
            <h3>Admin Roles</h3>
            <ComponentLoader isLoading={isLoading} />
            {!isLoading &&
                <Fragment>
                    {!!roles && roles.map(role => {
                        return (
                            <Accordion
                                expanded={expanded === `panel${role.id}`}
                                onChange={handleChange(`panel${role.id}`)}
                                key={role.id}
                            >
                                <AccordionSummary
                                    expandIcon={<ExpandMoreIcon />}
                                    aria-controls={`panel${role.id}-content`}
                                    id={`panel${role.id}-header`}
                                >
                                    <Avatar
                                        className={classes.avatar}
                                        style={{ backgroundColor: "#3B3B3B" }}
                                    >
                                        {role.name.substring(0, 1).toUpperCase()}
                                    </Avatar>
                                    <Typography className={classes.heading}>{role.name}</Typography>
                                    {EDIT_ACCESS && expanded === `panel${role.id}` && role.id !== 1 &&
                                        <Box style={{ flex: 1, display: "flex", justifyContent: "flex-end" }}>
                                            <Box display="flex" flexWrap="no-wrap">
                                                <IconButton
                                                    className={classes.button}
                                                    aria-label="edit-role"
                                                    onClick={() => editButtonClick(role)}
                                                    size="small"
                                                >
                                                    <EditIcon />
                                                </IconButton>
                                                {DELETE_ACCESS &&
                                                    <IconButton
                                                        className={classes.button}
                                                        aria-label="delete-role"
                                                        onClick={() => deleteButtonClick(role)}
                                                        size="small"
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>}
                                            </Box>
                                        </Box>}

                                </AccordionSummary>
                                <AccordionDetails>
                                    <Paper className={classes.paper}>
                                        {!!role.permissions && role.permissions.length > 0 && (
                                            <Table className={classes.table} size="small">
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell>Permission</TableCell>
                                                        <TableCell>Type</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {role.permissions.map(p => {
                                                        return (
                                                            <TableRow key={p.permissionId}>
                                                                <TableCell>{p.permissionName}</TableCell>
                                                                <TableCell>{getType(p.type)}</TableCell>
                                                            </TableRow>)
                                                    })}
                                                </TableBody>
                                            </Table>)}
                                    </Paper>
                                </AccordionDetails>
                            </Accordion>)
                    })}
                    {EDIT_ACCESS &&
                        <Fragment>
                            <Box
                                justifyContent="center"
                                display={isLoading ? "none" : "flex"}
                                style={{ marginTop: "15px" }}
                            >
                                <Fab
                                    size="small"
                                    color="primary"
                                    aria-label="add"
                                    onClick={addButtonClick}
                                >
                                    <AddIcon />
                                </Fab>
                            </Box>
                            <AddEditRoleForm
                                action={addEditParams.action}
                                openModal={openModal}
                                handleCloseModal={handleCloseModal}
                                initialValues={addEditParams.initialValues}
                                roleId={addEditParams.roleId}
                            />
                        </Fragment>
                    }
                    {DELETE_ACCESS &&
                        <DeleteRoleForm
                            openModal={openDeleteModal}
                            handleCloseModal={handleCloseDeleteModal}
                            roleId={deleteParams.roleId}
                            roleName={deleteParams.roleName}
                        />}
                </Fragment>}
        </Container>
    )
}
